import { OrderType } from '@/enums/OrderEnums';
import { TCoversPromptMode } from '@/interfaces/IBranch';
import { IOrder } from '@/interfaces/IOrder';
import { isNewOrder, isPendingOrder } from '@/utils/orders.utils';

export interface ICoversAcceptPromptTarget {
  sessionId: string;
  /** Customer-suggested party size to prefill the prompt with, if any. */
  suggested: number | null;
}

/**
 * Post-accept covers prompt gate (mirrors the `-pos` adopt-flow rule): after
 * staff accepts an incoming Seat order, prompt for covers only when the
 * order's session has no confirmed value yet AND the order is the table's
 * only live order — any other pending/incoming order on the table means
 * mid-service, where the table-header chip is the right place to edit.
 *
 * @returns the session to confirm covers on, or null when no prompt is due.
 */
export function getCoversAcceptPromptTarget(params: {
  order: IOrder;
  /** Live branch orders (new + pending), typically the orders store's set. */
  liveOrders: IOrder[] | null | undefined;
  coversPromptMode: TCoversPromptMode | null | undefined;
}): ICoversAcceptPromptTarget | null {
  const { order, liveOrders } = params;

  if ((params.coversPromptMode ?? 'optional') === 'off') return null;
  if (order.type !== OrderType.Seat || order.tableId == null) return null;

  const session = order.tableSession;
  if (!session || session.covers != null) return null;

  const hasOtherLiveOrder = (liveOrders ?? []).some(
    (other) =>
      other.id !== order.id &&
      other.type === OrderType.Seat &&
      other.tableId === order.tableId &&
      (isNewOrder(other) || isPendingOrder(other))
  );
  if (hasOtherLiveOrder) return null;

  return {
    sessionId: session.id,
    suggested: session.coversSuggested ?? order.coversSuggested ?? null,
  };
}
